import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import api from "../lib/api";
import { toast } from "sonner";
import { Cpu, Copy, RefreshCw, Eye, EyeOff, Terminal, BookOpen, ExternalLink, Webhook, Send } from "lucide-react";

const ENDPOINTS = [
    { method: "GET", suffix: "/restaurant", purpose: "Verifica chiave + identità del ristorante" },
    { method: "GET", suffix: "/reservations", purpose: "Lista prenotazioni · query: from_date, to_date, status, limit" },
    { method: "GET", suffix: "/tables", purpose: "Layout tavoli (per il floor map)" },
    { method: "POST", suffix: "/snapshot", purpose: "Push stato live: {tables:[…], reservations:[…]}" },
    { method: "POST", suffix: "/reservations/{id}/confirm", purpose: "Conferma una prenotazione" },
    { method: "POST", suffix: "/reservations/{id}/cancel", purpose: "Cancella una prenotazione" },
    { method: "POST", suffix: "/reservations/{id}/seat", purpose: "Cliente arrivato e seduto" },
    { method: "PATCH", suffix: "/reservations/{id}", purpose: "Aggiorna status / arrival_status / table_id / notes" },
    { method: "POST", suffix: "/tables/{id}/status", purpose: "Stato tavolo: free | reserved | occupied" },
];

export default function AdminIntegrations() {
    const [info, setInfo] = useState(null);
    const [keyError, setKeyError] = useState(null);
    const [showKey, setShowKey] = useState(false);
    const [rotating, setRotating] = useState(false);

    // Webhook state
    const [webhook, setWebhook] = useState(null);
    const [webhookUrl, setWebhookUrl] = useState("");
    const [webhookEnabled, setWebhookEnabled] = useState(true);
    const [savingWebhook, setSavingWebhook] = useState(false);
    const [testingWebhook, setTestingWebhook] = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    const [secretValue, setSecretValue] = useState("");

    useEffect(() => {
        api.get("/admin/api-key")
            .then((r) => { setInfo(r.data); setKeyError(null); })
            .catch((err) => {
                if (err.response?.status === 404) {
                    setKeyError("Endpoint non disponibile su questo deploy. Aggiorna la versione di produzione (clicca \"Deploy\" dalla chat Emergent).");
                } else {
                    setKeyError(err.response?.data?.detail || "Impossibile leggere la chiave");
                }
            });

        api.get("/admin/webhook")
            .then((r) => {
                setWebhook(r.data);
                setWebhookUrl(r.data?.url || "");
                setWebhookEnabled(r.data?.enabled !== false);
            })
            .catch(() => {});  // soft fail: webhook is optional
    }, []);

    const externalBaseURL = window.location.origin + (info?.external_base_url || "/api/external/v1");
    const sampleCurl = info ? `curl -H "Authorization: Bearer ${info.api_key}" \\
  "${externalBaseURL}/reservations?from_date=2026-04-01&to_date=2026-12-31"` : "";

    const copy = (txt, label) => {
        navigator.clipboard.writeText(txt).then(
            () => toast.success(`${label} copiato negli appunti`),
            () => toast.error("Impossibile copiare"),
        );
    };

    const rotate = async () => {
        if (!window.confirm("Generare una nuova chiave? Quella attuale verrà invalidata immediatamente."))
            return;
        setRotating(true);
        try {
            const { data } = await api.post("/admin/api-key/rotate");
            setInfo((prev) => ({ ...prev, api_key: data.api_key }));
            toast.success("Nuova chiave generata");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setRotating(false);
        }
    };

    const saveWebhook = async () => {
        setSavingWebhook(true);
        try {
            const { data } = await api.put("/admin/webhook", {
                url: webhookUrl.trim(),
                enabled: webhookEnabled,
            });
            setSecretValue(data.secret || "");
            // refresh meta
            const r = await api.get("/admin/webhook");
            setWebhook(r.data);
            toast.success(webhookUrl ? "Webhook salvato" : "Webhook rimosso");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Impossibile salvare");
        } finally {
            setSavingWebhook(false);
        }
    };

    const testWebhook = async () => {
        setTestingWebhook(true);
        try {
            const { data } = await api.post("/admin/webhook/test");
            const ld = data?.last_delivery;
            if (ld?.status_code && ld.status_code >= 200 && ld.status_code < 300) {
                toast.success(`Test OK · ${ld.status_code}`);
            } else if (ld?.error) {
                toast.error(`Test fallito: ${ld.error}`);
            } else {
                toast.error(`Test fallito · stato ${ld?.status_code ?? "n/d"}`);
            }
            const r = await api.get("/admin/webhook");
            setWebhook(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setTestingWebhook(false);
        }
    };

    const rotateSecret = async () => {
        if (!window.confirm("Rigenerare il segreto del webhook? Quello attuale smetterà di firmare i prossimi eventi."))
            return;
        try {
            const { data } = await api.post("/admin/webhook/rotate-secret");
            setSecretValue(data.secret);
            toast.success("Nuovo secret generato");
            const r = await api.get("/admin/webhook");
            setWebhook(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    return (
        <div className="flex min-h-screen bg-ink" data-testid="admin-integrations">
            <AdminSidebar />
            <main className="flex-1 p-10 space-y-10">
                <div>
                    <div className="text-gold text-xs tracking-[0.4em] uppercase">Integrazioni</div>
                    <h1 className="font-serif text-5xl text-white mt-2">SmartTable & API</h1>
                    <p className="text-gray-400 mt-3 max-w-2xl">
                        Collega il tuo gestionale, la tua app SmartTable o un POS esterno per leggere le prenotazioni di Prenova in tempo reale. Ogni ristorante ha la sua <strong className="text-gold">API key personale</strong>: tienila al sicuro.
                    </p>
                </div>

                {/* SmartTable promo card */}
                <div className="bg-gradient-to-br from-[#0f1730] via-[#0a0a0c] to-[#1a1f3a] border border-gold/30 rounded-sm p-6 sm:p-8" data-testid="smarttable-promo">
                    <div className="flex items-start gap-5 flex-wrap">
                        <div className="bg-white/95 rounded-sm p-3 shrink-0" data-testid="smarttable-logo">
                            <img src="/smarttable-logo.png" alt="SmartTable 360 by renova 360" className="h-14 w-auto object-contain" />
                        </div>
                        <div className="flex-1 min-w-[260px]">
                            <div className="text-[10px] uppercase tracking-[0.4em] text-gold/80 font-semibold">Renova gestione tavoli</div>
                            <h2 className="font-serif text-3xl text-white mt-0.5">SmartTable 360</h2>
                            <p className="text-sm text-gray-300 mt-3 leading-relaxed max-w-2xl">
                                Sincronizza le prenotazioni di Prenova con la tua gestione di sala: comande, conti tavolo, mappa interattiva, POS. Configura SmartTable copiando la tua API key qui sotto nella sezione Impostazioni → Integrazioni dell'app.
                            </p>
                            <a
                                href="https://service-hub-899.emergent.host"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 mt-4 bg-gold text-ink px-5 py-2.5 rounded-sm tracking-widest uppercase text-xs font-semibold hover:bg-gold/90 transition-colors"
                                data-testid="smarttable-open-link"
                            >
                                <ExternalLink size={14} /> Accedi e configura SmartTable
                            </a>
                        </div>
                    </div>
                </div>

                {/* Error banner if key endpoint unavailable */}
                {keyError && (
                    <div className="bg-amber-500/10 border border-amber-500/40 rounded-sm p-4 text-sm text-amber-100" data-testid="api-key-error">
                        <strong>Attenzione:</strong> {keyError}
                    </div>
                )}

                {/* SmartTable quick-setup: 3 valori pronti da incollare */}
                <div className="bg-ink-surface border border-gold/40 rounded-sm p-6 sm:p-8" data-testid="smarttable-setup">
                    <h2 className="font-serif text-2xl text-white">Configurazione SmartTable</h2>
                    <p className="text-xs text-gray-400 mt-1 mb-5">Apri SmartTable → Impostazioni → Integrazioni → Prenova, e incolla questi 3 valori esattamente come li vedi.</p>

                    <div className="space-y-3">
                        {[
                            { label: "API URL (base)", value: externalBaseURL, testid: "field-api-url", hint: "Tutti gli endpoint partono da qui." },
                            { label: "API Key", value: info?.api_key || "—", testid: "field-api-key", hint: "Header: Authorization: Bearer <api-key>", monoGold: true },
                            { label: "Restaurant ID", value: info?.restaurant_id || "—", testid: "field-rest-id" },
                            { label: "Push Path (POST snapshot)", value: `${externalBaseURL}/snapshot`, testid: "field-push-path", hint: "URL dove SmartTable POSTA stato tavoli e prenotazioni." },
                            { label: "Pull Path (GET prenotazioni)", value: `${externalBaseURL}/reservations`, testid: "field-pull-path", hint: "URL da cui SmartTable LEGGE le prenotazioni." },
                        ].map((row) => (
                            <div key={row.testid} className="bg-ink border border-white/10 rounded-sm p-3 sm:p-4">
                                <div className="flex items-center justify-between gap-3 mb-1.5 flex-wrap">
                                    <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-semibold">{row.label}</span>
                                    <button
                                        onClick={() => info && copy(row.value, row.label)}
                                        disabled={!info}
                                        className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gold tracking-widest uppercase disabled:opacity-40"
                                        data-testid={`copy-${row.testid}`}
                                    >
                                        <Copy size={12} /> Copia
                                    </button>
                                </div>
                                <code className={`block font-mono text-sm break-all ${row.monoGold ? "text-gold" : "text-gray-200"}`} data-testid={row.testid}>
                                    {row.value}
                                </code>
                                {row.hint && <div className="text-[11px] text-gray-500 mt-1.5">{row.hint}</div>}
                            </div>
                        ))}
                    </div>
                </div>

                {/* API key card */}
                <div className="bg-ink-surface border border-white/10 rounded-sm p-6 sm:p-8" data-testid="api-key-card">
                    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                        <div>
                            <h2 className="font-serif text-2xl text-white">La tua API key</h2>
                            <p className="text-xs text-gray-500 mt-1">Trattala come una password — chiunque la abbia può leggere le tue prenotazioni.</p>
                        </div>
                        <button onClick={rotate} disabled={rotating} data-testid="rotate-key-btn"
                            className="inline-flex items-center gap-2 text-xs text-amber-300 border border-amber-300/30 hover:bg-amber-300/10 px-4 py-2.5 rounded-sm tracking-widest uppercase font-semibold disabled:opacity-50">
                            <RefreshCw size={13} className={rotating ? "animate-spin" : ""} /> Rigenera
                        </button>
                    </div>

                    <div className="bg-ink border border-white/10 rounded-sm p-4 flex items-center gap-3 font-mono text-sm break-all">
                        <code className="flex-1 text-gold" data-testid="api-key-value">
                            {info ? (showKey ? info.api_key : "•".repeat(Math.min(48, info.api_key.length))) : "…"}
                        </code>
                        <button onClick={() => setShowKey((v) => !v)} className="text-gray-400 hover:text-white shrink-0" data-testid="toggle-key-visibility" title={showKey ? "Nascondi" : "Mostra"}>
                            {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <button onClick={() => info && copy(info.api_key, "API key")} className="text-gray-400 hover:text-gold shrink-0" data-testid="copy-key-btn" title="Copia">
                            <Copy size={16} />
                        </button>
                    </div>

                    <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs">
                        <div className="bg-ink border border-white/10 rounded-sm p-3">
                            <div className="text-[10px] uppercase tracking-widest text-gray-500">Restaurant ID</div>
                            <div className="text-gray-300 font-mono mt-1 break-all" data-testid="restaurant-id-value">{info?.restaurant_id || "—"}</div>
                        </div>
                        <div className="bg-ink border border-white/10 rounded-sm p-3">
                            <div className="text-[10px] uppercase tracking-widest text-gray-500">Endpoint base</div>
                            <div className="text-gray-300 font-mono mt-1 break-all" data-testid="external-base-url">{externalBaseURL}</div>
                        </div>
                    </div>
                </div>

                {/* Endpoints reference */}
                <div className="bg-ink-surface border border-white/10 rounded-sm p-6 sm:p-8" data-testid="endpoints-card">
                    <h2 className="font-serif text-2xl text-white mb-1">Endpoint disponibili</h2>
                    <p className="text-xs text-gray-500 mb-5">Autenticazione: header <code className="text-gold">Authorization: Bearer &lt;la-tua-api-key&gt;</code> oppure <code className="text-gold">X-Api-Key: &lt;la-tua-api-key&gt;</code></p>
                    <div className="divide-y divide-white/5">
                        {ENDPOINTS.map((ep) => {
                            const fullUrl = `${externalBaseURL}${ep.suffix}`;
                            const slug = ep.suffix.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "");
                            return (
                                <div key={`${ep.method}-${ep.suffix}`} className="py-4 grid sm:grid-cols-12 gap-3 items-center">
                                    <span className={`sm:col-span-1 text-[10px] tracking-widest uppercase font-semibold ${ep.method === "GET" ? "text-emerald-300" : ep.method === "PATCH" ? "text-sky-300" : "text-gold"}`}>{ep.method}</span>
                                    <code className="sm:col-span-7 text-gold font-mono text-xs break-all" data-testid={`endpoint-${slug}`}>{fullUrl}</code>
                                    <span className="sm:col-span-3 text-xs text-gray-400">{ep.purpose}</span>
                                    <button
                                        onClick={() => copy(fullUrl, "Endpoint")}
                                        className="sm:col-span-1 justify-self-end text-gray-500 hover:text-gold"
                                        title="Copia endpoint"
                                        data-testid={`copy-endpoint-${slug}`}
                                    >
                                        <Copy size={14} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Quickstart curl */}
                <div className="bg-ink-surface border border-white/10 rounded-sm p-6 sm:p-8" data-testid="quickstart-card">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Terminal size={18} className="text-gold" strokeWidth={1.4} />
                            <h2 className="font-serif text-2xl text-white">Esempio rapido (curl)</h2>
                        </div>
                        <button onClick={() => copy(sampleCurl, "Comando")} className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gold" data-testid="copy-curl-btn">
                            <Copy size={13} /> Copia
                        </button>
                    </div>
                    <pre className="bg-black/60 border border-white/5 rounded-sm p-4 text-xs text-emerald-200 font-mono whitespace-pre-wrap break-all" data-testid="sample-curl">{sampleCurl || "…"}</pre>

                    <div className="mt-5 flex gap-3 text-xs flex-wrap">
                        <a href="/api/openapi.json" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-gray-400 hover:text-gold tracking-widest uppercase">
                            <BookOpen size={13} /> OpenAPI JSON
                        </a>
                        <a href={`${externalBaseURL}/restaurant`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-gray-400 hover:text-gold tracking-widest uppercase">
                            <ExternalLink size={13} /> Test endpoint
                        </a>
                    </div>
                </div>

                {/* Webhook outbound (Prenova → SmartTable) */}
                <div className="bg-ink-surface border border-white/10 rounded-sm p-6 sm:p-8" data-testid="webhook-card">
                    <div className="flex items-center gap-2 mb-1">
                        <Webhook size={20} className="text-gold" strokeWidth={1.4} />
                        <h2 className="font-serif text-2xl text-white">Webhook in uscita (Prenova → SmartTable)</h2>
                    </div>
                    <p className="text-xs text-gray-400 mb-5 max-w-2xl">
                        Quando un cliente prenota, conferma o disdice, Prenova chiama questo URL via <code className="text-gold">POST</code> in tempo reale con un payload JSON firmato HMAC-SHA256.
                        Eventi inviati: <code className="text-gold">reservation.created</code>, <code className="text-gold">.confirmed</code>, <code className="text-gold">.cancelled</code>, <code className="text-gold">.arrived</code>, <code className="text-gold">.no_show</code>.
                    </p>

                    <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-1.5">Webhook URL</label>
                    <div className="flex gap-2 flex-wrap">
                        <input
                            type="url"
                            value={webhookUrl}
                            onChange={(e) => setWebhookUrl(e.target.value)}
                            placeholder="https://smarttable.example.com/api/webhooks/prenova"
                            className="flex-1 min-w-[260px] bg-ink border border-white/10 rounded-sm px-3 py-2.5 font-mono text-sm text-gray-100 focus:border-gold/60 outline-none"
                            data-testid="webhook-url-input"
                        />
                        <label className="inline-flex items-center gap-2 text-xs text-gray-300 px-3 py-2.5 border border-white/10 rounded-sm select-none cursor-pointer">
                            <input
                                type="checkbox"
                                checked={webhookEnabled}
                                onChange={(e) => setWebhookEnabled(e.target.checked)}
                                data-testid="webhook-enabled-toggle"
                            />
                            Attivo
                        </label>
                        <button
                            onClick={saveWebhook}
                            disabled={savingWebhook}
                            className="inline-flex items-center gap-2 bg-gold text-ink px-5 py-2.5 rounded-sm tracking-widest uppercase text-xs font-semibold disabled:opacity-50"
                            data-testid="webhook-save-btn"
                        >
                            {savingWebhook ? "Salvo…" : "Salva"}
                        </button>
                        <button
                            onClick={testWebhook}
                            disabled={testingWebhook || !webhook?.url}
                            className="inline-flex items-center gap-2 border border-emerald-300/40 text-emerald-300 hover:bg-emerald-300/10 px-4 py-2.5 rounded-sm tracking-widest uppercase text-xs font-semibold disabled:opacity-40"
                            data-testid="webhook-test-btn"
                            title={webhook?.url ? "Invia un evento di test" : "Salva prima un URL"}
                        >
                            <Send size={13} /> {testingWebhook ? "Invio…" : "Test ping"}
                        </button>
                    </div>

                    {/* Secret + last delivery */}
                    <div className="mt-5 grid sm:grid-cols-2 gap-3 text-xs">
                        <div className="bg-ink border border-white/10 rounded-sm p-3" data-testid="webhook-secret-card">
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-[10px] uppercase tracking-widest text-gray-500">Webhook secret (HMAC-SHA256)</span>
                                <div className="flex gap-2">
                                    {(secretValue || webhook?.secret_set) && (
                                        <button onClick={() => setShowSecret((v) => !v)} className="text-gray-400 hover:text-white" title={showSecret ? "Nascondi" : "Mostra"}>
                                            {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                                        </button>
                                    )}
                                    {secretValue && (
                                        <button onClick={() => copy(secretValue, "Secret")} className="text-gray-400 hover:text-gold" title="Copia">
                                            <Copy size={13} />
                                        </button>
                                    )}
                                    <button onClick={rotateSecret} className="text-amber-300 hover:text-amber-200" title="Rigenera">
                                        <RefreshCw size={13} />
                                    </button>
                                </div>
                            </div>
                            <code className="block font-mono text-gold break-all" data-testid="webhook-secret-value">
                                {secretValue
                                    ? (showSecret ? secretValue : "•".repeat(40))
                                    : (webhook?.secret_set ? (showSecret ? "(salva di nuovo per visualizzare)" : webhook.secret_preview || "•••••••••") : "—")}
                            </code>
                            <div className="text-[11px] text-gray-500 mt-1.5">Configuralo in SmartTable per verificare la firma <code className="text-gold">X-Prenova-Signature: sha256=…</code></div>
                        </div>

                        <div className="bg-ink border border-white/10 rounded-sm p-3" data-testid="webhook-last-delivery">
                            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Ultima consegna</div>
                            {webhook?.last_delivery ? (
                                <div className="text-gray-300 space-y-0.5">
                                    <div><span className="text-gray-500">Evento:</span> <code className="text-gold">{webhook.last_delivery.event}</code></div>
                                    <div>
                                        <span className="text-gray-500">Stato:</span>{" "}
                                        {webhook.last_delivery.status_code
                                            ? <code className={webhook.last_delivery.status_code < 300 ? "text-emerald-300" : "text-red-300"}>{webhook.last_delivery.status_code}</code>
                                            : <code className="text-red-300">errore</code>}
                                    </div>
                                    {webhook.last_delivery.error && <div className="text-red-300 break-all"><span className="text-gray-500">Errore:</span> {webhook.last_delivery.error}</div>}
                                    <div className="text-[11px] text-gray-500">{new Date(webhook.last_delivery.at).toLocaleString("it-IT")}</div>
                                </div>
                            ) : (
                                <div className="text-gray-500">Nessuna consegna ancora.</div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
